/**
 * FilterEditor Type Definitions
 *
 * Shared types used across FilterEditor components
 *
 * @module FilterEditor/types
 */

import type { LinkedTableFilterApi, TableFieldApiResponse, FilterOperator, LinkedTableVariableApi } from '@loader/shared-types';

/** Value/label pair for field values */
export interface FieldValueOption {
  value: string;
  label: string;
}

/**
 * Linked table field values API response
 * Returned by GET /api/private/linked-tables/:id/field-values/:fieldId
 */
export interface LinkedTableFieldValuesResponse {
  field_id: string;
  field_name: string;
  field_type?: string;
  data_type: string;
  message?: string;
  values: FieldValueOption[];
  total: number;
}

/**
 * Default operator labels (used for text-like and generic types)
 */
const DEFAULT_OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  greater_than: 'is greater than',
  less_than: 'is less than',
  greater_than_or_equal: 'is at least',
  less_than_or_equal: 'is at most',
  in: 'is any of',
  not_in: 'is none of',
  is_null: 'is empty',
  is_not_null: 'is not empty',
};

/**
 * Type-specific label overrides for operators that read differently per data type
 */
const TYPE_LABEL_OVERRIDES: Partial<Record<string, Partial<Record<FilterOperator, string>>>> = {
  date: {
    greater_than: 'is after',
    less_than: 'is before',
    greater_than_or_equal: 'is on or after',
    less_than_or_equal: 'is on or before',
  },
  datetime: {
    greater_than: 'is after',
    less_than: 'is before',
    greater_than_or_equal: 'is on or after',
    less_than_or_equal: 'is on or before',
  },
  number: {
    greater_than: '>',
    less_than: '<',
    greater_than_or_equal: '≥',
    less_than_or_equal: '≤',
  },
  boolean: {
    equals: 'is',
    not_equals: 'is not',
  },
};

/**
 * Get the display label for an operator given a field data type.
 */
export function getOperatorLabel(operator: FilterOperator, dataType?: string): string {
  const overrides = dataType ? TYPE_LABEL_OVERRIDES[dataType] : undefined;
  return overrides?.[operator] ?? DEFAULT_OPERATOR_LABELS[operator] ?? operator;
}

export interface FilterEditorProps {
  /** Whether the user can edit (false for viewers) */
  canEdit?: boolean;
  /** The filter to edit */
  filter: LinkedTableFilterApi;
  /** Available fields for filtering */
  availableFields: TableFieldApiResponse[];
  /** LinkedTable ID to fetch field values from (unfiltered) */
  linkedTableId?: string;
  /** Unified version selections keyed by table alias */
  versionSelections?: Record<string, string>;
  /** Whether this filter is incomplete (should be highlighted) */
  isIncomplete?: boolean;
  /** Available variables for filter binding */
  variables?: LinkedTableVariableApi[];
  /** Callback when filter is updated */
  onUpdate: (filter: LinkedTableFilterApi) => void;
  /** Callback when filter is removed */
  onRemove: (filter: LinkedTableFilterApi) => void;
}

export interface ValueSelectorProps {
  /** Whether the user can edit (false for viewers) */
  canEdit?: boolean;
  filter: LinkedTableFilterApi;
  selectedField: TableFieldApiResponse | undefined;
  linkedTableId?: string;
  versionSelections?: Record<string, string>;
  onUpdate: (filter: LinkedTableFilterApi) => void;
  debouncedOnUpdate: (filter: LinkedTableFilterApi) => void;
}
